import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import dayjs from '../../config/dayjs';
import SelectDaysButtons from '../buttons/SelectDaysButtons';
import {
  DEFAULT_CALENDAR_MIN_DAYS_FROM_TODAY,
  TODAY,
  daysFromTodayToDate,
  getDayDifference,
} from '../../constants';
import {
  getDebtorAveragePaymentDays,
  getEffectiveMaxDate,
  getDefaultFundingDays,
} from '../../helpers/date-to-pay-restrictions';
import useGetCountryFromUrl from '../../hooks/useGetCountryFromUrl';
import { useCartDateDisableCheck } from '../../hooks/useUpcomingHolidays';

const OTHER_INPUT_SELECTED = -1;

function isNotManualSelection(selectedDays, daysToPay) {
  return selectedDays !== OTHER_INPUT_SELECTED && daysToPay !== null && daysToPay !== 0;
}

const SelectDaysButtonsCell = ({
  params,
  selectedDaysButtonsOnChange,
  selectedDaysButtonsOnRecommendedPaymentTerm,
  daysButtons,
  isMobile,
  unselectButtons,
  sameDateForAllSelection,
  minDaysFromToday,
}) => {
  const formatLabelDays = useCallback(
    (slideDays) => (slideDays === 0 || slideDays === null ? '' : `${slideDays}`),
    [],
  );

  const country = useGetCountryFromUrl();
  const shouldDisableDate = useCartDateDisableCheck();
  const [selectedDays, setSelectedDays] = useState(null);
  const [daysToPay, setDaysToPay] = useState(null);
  const [dateValue, setDateValue] = useState(null);

  useEffect(() => {
    if (sameDateForAllSelection) {
      const daysDifference = getDayDifference(sameDateForAllSelection, TODAY);
      setDaysToPay(daysDifference);
      if (daysButtons.some((button) => button.days === daysDifference)) {
        setSelectedDays(daysDifference);
      } else {
        setSelectedDays(OTHER_INPUT_SELECTED);
        setDateValue(sameDateForAllSelection);
      }
    }
  }, [sameDateForAllSelection]);

  const debtorAveragePaymentDays = useMemo(
    () => {
      const avaragePaymentDaysFromDateIssued = getDebtorAveragePaymentDays(
        params?.row?.invoicesActualPaymentTerm,
      );
      return avaragePaymentDaysFromDateIssued;
    },
    [params.row],
  );

  const maxDate = useMemo(() => getEffectiveMaxDate(params.row, country), [params.row, country]);

  const pickerMinDate = useMemo(
    () => daysFromTodayToDate(minDaysFromToday),
    [minDaysFromToday],
  );

  const datePickerOnChange = (newValue) => {
    setDateValue(newValue);
    selectedDaysButtonsOnChange(newValue, params.row);
  };

  const datePickerOnRecommendedPaymentTerm = (newValue) => {
    selectedDaysButtonsOnRecommendedPaymentTerm(newValue, params.row);
  };

  useEffect(() => {
    if (isNotManualSelection(selectedDays, daysToPay)) {
      const newDate = daysFromTodayToDate(daysToPay);
      datePickerOnChange(newDate);
    }
  }, [daysToPay, selectedDays]);

  return (
    <Grid
      container
      columnSpacing={1}
      rowSpacing={0}
      id="select-days-buttons-cell"
    >
      <SelectDaysButtons
        daysButtons={daysButtons}
        selectedDays={selectedDays}
        setSelectedDays={setSelectedDays}
        setDaysToPay={setDaysToPay}
        formatLabelDays={formatLabelDays}
        daysToPay={daysToPay}
        maxDaysToPay={maxDate?.diff(TODAY, 'days') || getDefaultFundingDays(country)}
        showButtonSubtitle={!isMobile}
        showDatePicker
        datePickerProps={{
          ...params,
          maxDate,
          minDate: pickerMinDate,
          debtorAveragePaymentDays,
          value: dateValue,
          onChange: datePickerOnChange,
          onRecommendedPaymentTerm: datePickerOnRecommendedPaymentTerm,
          shouldDisableWeekend: shouldDisableDate,
        }}
        buttonProps={{
          sx: {
            width: { xs: 45, md: 50 },
            minWidth: { xs: 45, md: 50 },
            p: 0,
          },
        }}
        rowProps={{
          sx: {
            height: { xs: 35, md: 50 },
          },
        }}
        useSmallDaysButtons
        unselectButtons={unselectButtons}
      />
    </Grid>
  );
};

SelectDaysButtonsCell.propTypes = {
  params: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    row: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      clientOffers: PropTypes.arrayOf(PropTypes.string).isRequired,
      invoicesActualPaymentTerm: PropTypes.number,
    }),
  }).isRequired,
  selectedDaysButtonsOnChange: PropTypes.func.isRequired,
  selectedDaysButtonsOnRecommendedPaymentTerm: PropTypes.func.isRequired,
  daysButtons: PropTypes.arrayOf(PropTypes.shape({
    days: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
  })).isRequired,
  isMobile: PropTypes.bool,
  unselectButtons: PropTypes.bool,
  sameDateForAllSelection: PropTypes.instanceOf(dayjs),
  minDaysFromToday: PropTypes.number,
};

SelectDaysButtonsCell.defaultProps = {
  isMobile: false,
  unselectButtons: false,
  sameDateForAllSelection: null,
  minDaysFromToday: DEFAULT_CALENDAR_MIN_DAYS_FROM_TODAY,
};

export default SelectDaysButtonsCell;
